import { vec3 } from 'gl-matrix';
import {
    gridSubRange,
    gridVoxelBounds,
    subRangeIsEmpty,
    VoxelSubRange,
    WorldBoundsMm,
} from '~/domain/services/imageService/gridGeometry';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { ContourTexture } from '../../../data/structure/ContourTexture';
import { RegionOfInterest } from '../../../data/structure/RegionOfInterest';
import { applyAsymmetricMarginCpu } from './AsymmetricMarginConvolution';
import { applyAsymmetricMarginGpuHierarchical } from './AsymmetricMarginGL';
import { SDFOperationRenderer } from './BooleanRenderer';
import { clearPartsTouchingWindow } from './partsTouchingWindow';
import { SdfGenerator } from './SdfGenerator';
import {
    ComponentSizeFloor,
    cropToBounds,
    CropDirection,
    fillCavities,
    fillCavities2D,
    keepLargestComponents,
    PostProcessingMode,
    RemoveSmallPartsOptions,
    removeSmallParts,
} from './SDFPostProcessing';
import { GlOperationGuard } from './utils';

export enum BooleanOperation {
    OR = 'or',
    AND = 'and',
    XOR = 'xor',
    AND_NOT = 'not',
}

/**
 * The crop region holds no part of the structure — either it resolved to an
 * EMPTY voxel window on the ROI's grid, or the window it resolved to
 * contains no foreground. Distinct from a GL failure (which returns
 * undefined) because the user-facing answer differs: this one is
 * "reposition the box", not "try again".
 *
 * The region is refused rather than widened into a workable one:
 * {@link gridSubRange} clamps every corner into the grid, so a box the user
 * never positioned on the anatomy would otherwise come back as a plausible
 * window and produce a plausible-looking result.
 */
export class EmptyCropRegionError extends Error {
    public constructor(message: string) {
        super(message);
        this.name = 'EmptyCropRegionError';
    }
}

/**
 * Another GPU operation holds {@link GlOperationGuard}, so this one never
 * started. Distinct from an `undefined` result, which means the operation
 * ran and produced nothing: a caller that can wait may re-offer the very
 * same run once the GPU frees up, where a produced-nothing result is
 * final.
 */
export class GlOperationBusyError extends Error {
    public constructor(operationName: string) {
        super(
            `"${operationName}" cannot start: another GL operation is running`
        );
        this.name = 'GlOperationBusyError';
    }
}

/**
 * What the component operations — {@link
 * SDFOperations.applyRemoveSmallParts} and {@link
 * SDFOperations.applyKeepLargestComponents} — report about a run. One
 * shape for both, so a caller that runs either can state one outcome.
 *
 * Tagged rather than "maybe a texture": only the 'changed' arm carries
 * one, so reaching a texture means having narrowed past the report that
 * nothing was removed. A run that removes nothing is a SUCCESS that
 * writes no texture, banks no undo entry and is still reported — the
 * distinction a bare `undefined` could not make.
 */
export type ComponentOperationOutcome =
    | {
          status: 'changed';
          texture: ContourTexture;
          numComponentsFound: number;
          numComponentsRemoved: number;
          /** See {@link RemoveSmallPartsResult.sizeFloorOverridden}. */
          sizeFloorOverridden: boolean;
      }
    | {
          status: 'unchanged';
          numComponentsFound: number;
          numComponentsRemoved: 0;
          sizeFloorOverridden: boolean;
      };

/** What {@link SDFOperations.applyFillCavities} did; see {@link ComponentOperationOutcome}. */
export type FillCavitiesOutcome =
    | { status: 'changed'; texture: ContourTexture; filledVoxelCount: number }
    | { status: 'unchanged'; filledVoxelCount: 0 };

/**
 * Boolean and morphological operations on SDF-based
 * {@link ContourTexture ContourTextures}. Use {@link applyBoolean}
 * to write a result into an existing destination (either a
 * {@link RegionOfInterest}'s texture or a {@link ContourTexture}
 * the caller owns). Use {@link applyBooleanToNewTexture} when no
 * destination exists yet.
 *
 * Internally each operation is implemented by a private
 * `(dest, other)` subfunction that mutates `dest` in place.
 * `applyBoolean` decides whether one of the source textures is
 * already the destination — in which case it dispatches in place
 * with no allocation — or whether a fresh texture must be built.
 */
/**
 * Clear whole parts touching `range`. Assumes the guard is held and the
 * range is non-empty.
 */
async function clearPartsInWindow(
    src: ContourTexture,
    range: VoxelSubRange
): Promise<{
    texture: ContourTexture;
    clearedVoxelCount: number;
    clearedPartCount: number;
}> {
    const sdf = src.signedDistanceField!;
    // The cached read, not `toArray`: on a warm field this is the array
    // already in memory, and the pass reads far less of it than a readback
    // would cost.
    const srcPixels = sdf.pixels;
    const { clearedSlices, clearedVoxelCount, clearedPartCount } =
        await clearPartsTouchingWindow(
            srcPixels,
            src.textureGrid.sizeX,
            src.textureGrid.sizeY,
            ContourTexture.SDF_THRESHOLD,
            range
        );

    if (clearedVoxelCount === 0) {
        // No new texture: the caller skips the tracked write entirely, so a
        // gesture over empty space leaves no undo entry.
        return { texture: src, clearedVoxelCount: 0, clearedPartCount: 0 };
    }

    // Untouched slices are handed on by REFERENCE — the upload reads them
    // and keeps nothing — so the copies this operation pays for are the
    // slices it actually rewrote.
    const outPixels = srcPixels.map(
        (slice, z) => clearedSlices.get(z) ?? slice
    );
    return {
        texture: src.cloneWithNewSdf(outPixels),
        clearedVoxelCount,
        clearedPartCount,
    };
}

export class SDFOperations {
    /**
     * Apply a boolean operation `src1 OP src2` and write the
     * result into `destination` (an ROI or a caller-owned
     * {@link ContourTexture}).
     *
     * In-place fast path is used when one of the sources IS the
     * destination's texture:
     *   - `src1 === destTexture`: dispatch `op(destTexture, src2)`.
     *   - `src2 === destTexture` and op is commutative
     *     (OR/AND/XOR): dispatch `op(destTexture, src1)`.
     * Otherwise a fresh texture is allocated, seeded from src1,
     * mutated by the subfunction, then either swapped into the
     * ROI (if `destination` is an ROI) or copied into the
     * caller's ContourTexture via `resetSdfAndGrid`.
     */
    public static applyBoolean(
        src1: ContourTexture,
        src2: ContourTexture,
        operation: BooleanOperation,
        destination: RegionOfInterest | ContourTexture
    ) {
        const isROI = destination instanceof RegionOfInterest;
        if (isROI) {
            destination.assertHasTexture();
        }
        const destTex: ContourTexture = isROI
            ? destination.texture!
            : destination;

        // Path:
        //   src1 === destTex                          → in-place
        //   src2 === destTex && op ∈ {OR, AND, XOR}   → in-place,
        //                                               swap args
        //   otherwise                                 → allocate
        let target: ContourTexture;
        let other: ContourTexture;
        let allocated = false;
        if (src1 === destTex) {
            target = destTex;
            other = src2;
        } else if (src2 === destTex && operation !== BooleanOperation.AND_NOT) {
            target = destTex;
            other = src1;
        } else {
            target = src1.unionAligned(src1);
            RenderStorage.getRenderer(SDFOperationRenderer).renderOnDestination(
                target,
                BooleanOperation.OR,
                src1
            );
            other = src2;
            allocated = true;
        }

        switch (operation) {
            case BooleanOperation.OR:
                this.or(target, other);
                break;
            case BooleanOperation.AND:
                this.and(target, other);
                break;
            case BooleanOperation.AND_NOT:
                this.andNot(target, other);
                break;
            case BooleanOperation.XOR:
                this.xor(target, other);
                break;
            default:
                throw new Error(`Unknown boolean operation: ${operation}`);
        }

        if (allocated) {
            if (isROI) {
                destination.texture!.delete();
                destination.texture = target;
            } else {
                // ContourTexture destination — preserve the caller's
                // reference; copy target's SDF into it via readback.
                destination.resetSdfAndGrid(
                    target.signedDistanceField.toArray(),
                    target.textureGrid
                );
                target.delete();
            }
        }

        const finalTex: ContourTexture = isROI
            ? destination.texture!
            : destination;
        finalTex.cropToContentBounds();
    }

    /**
     * Apply a boolean operation on two source textures and return
     * the result as a new {@link ContourTexture}. No
     * {@link RegionOfInterest} is modified. The caller owns the
     * returned texture and must call {@link ContourTexture.delete}
     * when done.
     *
     * Allocates a fresh result texture (cloned from src1) and
     * delegates to {@link applyBoolean}. Passing the freshly
     * cloned texture as both `src1` and `destination` makes
     * applyBoolean take its in-place fast path, so no second
     * allocation happens.
     */
    public static applyBooleanToNewTexture(
        src1: ContourTexture,
        src2: ContourTexture,
        operation: BooleanOperation
    ): ContourTexture {
        const result = src1.unionAligned(src1);
        RenderStorage.getRenderer(SDFOperationRenderer).renderOnDestination(
            result,
            BooleanOperation.OR,
            src1
        );
        this.applyBoolean(result, src2, operation, result);
        return result;
    }

    /**
     * `src1` AND or AND NOT `src2` as a new texture the caller owns, on all of
     * `src1`'s box: unlike {@link applyBooleanToNewTexture}, past the image too.
     */
    public static applyBooleanUncropped(
        src1: ContourTexture,
        src2: ContourTexture,
        operation: BooleanOperation.AND | BooleanOperation.AND_NOT
    ): ContourTexture {
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        const result = ContourTexture.createUnionAligned(src1, src1, 0, false);
        let cover: ContourTexture | undefined;
        try {
            renderer.renderOnDestination(result, BooleanOperation.OR, src1);
            if (operation === BooleanOperation.AND_NOT) {
                renderer.renderOnDestination(result, operation, src2);
                return result;
            }
            // A render leaves what its source does not reach, so the AND
            // takes a source covering all of the result; see `and`.
            cover = ContourTexture.createUnionAligned(result, src2, 0, false);
            renderer.renderOnDestination(cover, BooleanOperation.OR, src2);
            renderer.renderOnDestination(result, operation, cover);
            return result;
        } catch (error) {
            result.delete();
            throw error;
        } finally {
            cover?.delete();
        }
    }

    /**
     * In-place OR: `dest = dest ∪ other`. Expands `dest`'s grid
     * if `other` extends beyond it.
     */
    private static or(dest: ContourTexture, other: ContourTexture): void {
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        if (dest.fullBoundingBox.contains(other.fullBoundingBox)) {
            renderer.renderOnDestination(dest, BooleanOperation.OR, other);
            return;
        }
        // Expand dest to cover the union via a temp + readback.
        const temp = dest.unionAligned(other);
        renderer.renderOnDestination(temp, BooleanOperation.OR, dest, other);
        dest.resetSdfAndGrid(
            temp.signedDistanceField.toArray(),
            temp.textureGrid
        );
        temp.delete();
    }

    /**
     * In-place AND: `dest = dest ∩ other`.
     *
     * Because {@link SDFOperationRenderer.renderOnDestination} only
     * touches destination pixels covered by the source's cross-section,
     * an AND with a source whose grid is smaller than `dest`'s would
     * leave dest's pre-AND content visible in the uncovered region —
     * wrong for true intersection. To avoid that, build a temp source
     * sized to cover `dest`'s grid (via `unionAligned`), OR `other`
     * into it (leaving 0 outside `other`'s bounds), then render AND
     * with the temp as source. The 0 regions then correctly clamp
     * dest to 0 via `gl.MIN`.
     */
    private static and(dest: ContourTexture, other: ContourTexture): void {
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        if (other.fullBoundingBox.contains(dest.fullBoundingBox)) {
            // `other` already covers every voxel of `dest` — direct AND.
            renderer.renderOnDestination(dest, BooleanOperation.AND, other);
            return;
        }
        // Expand the source so it covers every voxel of dest. The
        // temp is initialised to 0; OR-ing `other` in places its
        // content where it belongs and leaves 0 in the regions
        // beyond `other`'s grid, which is exactly what AND needs.
        const temp = dest.unionAligned(other);
        renderer.renderOnDestination(temp, BooleanOperation.OR, other);
        renderer.renderOnDestination(dest, BooleanOperation.AND, temp);
        temp.delete();
    }

    /** In-place AND_NOT: `dest = dest \ other`. */
    private static andNot(dest: ContourTexture, other: ContourTexture): void {
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        renderer.renderOnDestination(dest, BooleanOperation.AND_NOT, other);
    }

    /**
     * In-place XOR: `dest = (dest ∪ other) \ (dest ∩ other)`.
     * Snapshots `dest ∩ other` into a temp before mutating dest,
     * then unions other into dest, then subtracts the snapshot.
     */
    private static xor(dest: ContourTexture, other: ContourTexture): void {
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);

        // andTemp = dest ∩ other, captured before dest is mutated.
        const andTemp = ContourTexture.createIntersectionAligned(dest, other);
        try {
            if (andTemp) {
                renderer.renderOnDestination(
                    andTemp,
                    BooleanOperation.OR,
                    dest
                );
                renderer.renderOnDestination(
                    andTemp,
                    BooleanOperation.AND,
                    other
                );
            }

            this.or(dest, other);

            if (andTemp) {
                renderer.renderOnDestination(
                    dest,
                    BooleanOperation.AND_NOT,
                    andTemp
                );
            }
        } finally {
            andTemp?.delete();
        }
    }

    /**
     * Compute a ring structure as the boolean difference of two
     * symmetric margins applied to the source surface:
     *   ring = margin(src, upperMm) AND_NOT margin(src, lowerMm)
     *
     * Both boundaries are signed distances from the surface:
     *   positive = expand outward, negative = contract inward,
     *   0 = the original surface.
     *
     * @param src Source contour texture with valid SDF
     * @param lowerMarginMm Lower (inner) boundary in mm, signed
     * @param upperMarginMm Upper (outer) boundary in mm, signed;
     *        must be strictly greater than lowerMarginMm
     * @returns New ContourTexture containing the ring region
     */
    public static async applyRing(
        src: ContourTexture,
        lowerMarginMm: number,
        upperMarginMm: number
    ): Promise<ContourTexture> {
        if (upperMarginMm <= lowerMarginMm) {
            throw new Error(
                `Upper margin (${upperMarginMm}mm) must be ` +
                    `greater than lower margin (${lowerMarginMm}mm)`
            );
        }

        const upperTexture =
            upperMarginMm !== 0
                ? await this.applySymmetricMargin(src, upperMarginMm)
                : src;

        const lowerTexture =
            lowerMarginMm !== 0
                ? await this.applySymmetricMargin(src, lowerMarginMm)
                : src;

        // ring = upper AND_NOT lower
        // Preserve the source margin so SDF falloff quality is maintained
        // in the returned texture for downstream visualization and ops.
        const marginMm = src.margin ?? ContourTexture.DEFAULT_MARGIN;
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        const temp = upperTexture.unionAligned(lowerTexture, marginMm);
        renderer.renderOnDestination(temp, BooleanOperation.OR, upperTexture);
        renderer.renderOnDestination(
            temp,
            BooleanOperation.AND_NOT,
            lowerTexture
        );

        // Clean up intermediate textures (skip if we reused src)
        if (upperMarginMm !== 0) {
            (upperTexture as ContourTexture).delete();
        }
        if (lowerMarginMm !== 0) {
            (lowerTexture as ContourTexture).delete();
        }

        return temp;
    }

    public static async applySymmetricMargin(
        src: ContourTexture,
        marginMm: number
    ): Promise<ContourTexture> {
        const absMargin = Math.abs(marginMm);
        return this.applyMarginBase(
            src,
            marginMm,
            absMargin,
            (texture, falloff) =>
                SDFOperations.applySymmetricShift(
                    texture.signedDistanceField!.toArray(),
                    marginMm,
                    falloff
                )
        );
    }

    /**
     * Builds the padded "expanded" SDF that {@link applyAsymmetricMargin}
     * reads from: the source SDF re-rendered into a grid grown by
     * `maxBufferMm` on every side so the margin shift has room. The
     * expanded grid depends only on the source and buffer width, so the
     * caller builds it once and reuses it across previews and applies;
     * the caller also owns its lifetime and must `delete()` it.
     *
     * @param src Source contour texture with valid SDF
     * @param maxBufferMm Largest per-direction margin (mm) the expanded
     *        grid must accommodate
     * @returns New expanded ContourTexture
     */
    public static createAsymmetricExpanded(
        src: ContourTexture,
        maxBufferMm: number
    ): ContourTexture {
        if (!src.signedDistanceField) {
            throw new Error('Cannot apply margin: source has no SDF data');
        }
        if (src.signedDistanceField.size[2] === 0) {
            throw new Error('Cannot apply margin: source SDF is empty');
        }

        const expanded = ContourTexture.createNewWithMargin(maxBufferMm, src);
        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        renderer.renderOnDestination(expanded, BooleanOperation.OR, src);
        return expanded;
    }

    /**
     * Applies asymmetric margins to a pre-expanded SDF. Each LPS axis
     * can have independent positive and negative margins.
     *
     * Runs the GPU morphological dilation/erosion (max/min of the
     * SDF over an asymmetric ellipsoidal kernel) against the expanded
     * grid, falling back to the CPU implementation if the GPU path
     * fails.
     *
     * @param expanded Padded source SDF from
     *        {@link createAsymmetricExpanded}, sized to fit the margins
     * @param negLpsMarginMm Margins for negative LPS
     *        directions: [Right, Anterior, Inferior] mm
     * @param posLpsMarginMm Margins for positive LPS
     *        directions: [Left, Posterior, Superior] mm
     * @returns New ContourTexture with margins applied
     */
    public static async applyAsymmetricMargin(
        expanded: ContourTexture,
        negLpsMarginMm: vec3,
        posLpsMarginMm: vec3
    ): Promise<ContourTexture> {
        if (!expanded.signedDistanceField) {
            throw new Error('Cannot apply margin: source has no SDF data');
        }

        // Allocate the output texture up front so the GPU pipeline
        // can render directly into its SDF — no CPU round trip.
        const output = new ContourTexture(
            expanded.grid,
            expanded.signedDistanceField.size,
            expanded.textureGrid.getPosition(),
            expanded.margin
        );
        output.resetGrid(expanded.textureGrid);

        try {
            await applyAsymmetricMarginGpuHierarchical(
                expanded,
                negLpsMarginMm,
                posLpsMarginMm,
                output
            );
        } catch (e) {
            const msg =
                e instanceof Error
                    ? `${e.message}\n${e.stack ?? ''}`
                    : String(e);
            console.warn(
                'GPU asymmetric margin failed, falling back to CPU: ' + msg
            );
            const resultPixels = applyAsymmetricMarginCpu(
                expanded.signedDistanceField!.toArray(),
                expanded.textureGrid,
                negLpsMarginMm,
                posLpsMarginMm
            );
            output.resetSdfData(resultPixels);
        }

        // Caller is responsible for cropToImageBounds when the
        // result is being applied. Preview does not need it; the
        // renderer clips against image bounds at draw time.
        return output;
    }

    /**
     * Shared margin pipeline. Expands the grid, re-renders
     * the SDF with a wide falloff, applies the shift, then
     * compresses back to the default falloff.
     *
     * @param src              Source contour texture with valid SDF.
     * @param maxExpansionMm   Largest outward (positive) margin in mm.
     *                          Drives grid expansion. Negative or zero
     *                          means pure erosion (no expansion needed).
     * @param maxAbsMarginMm   Largest absolute margin in mm across all
     *                          directions. Drives the temporary falloff
     *                          width.
     * @param applyShift       Callback that transforms SDF pixels
     *                          into the shifted result. Receives
     *                          (texture, currentFalloffMm).
     * @returns New ContourTexture with the margin applied.
     */
    private static async applyMarginBase(
        src: ContourTexture,
        maxExpansionMm: number,
        maxAbsMarginMm: number,
        applyShift: (
            texture: ContourTexture,
            falloffMm: number
        ) => Uint8Array[] | Promise<Uint8Array[]>,
        widenFalloff: boolean = true
    ): Promise<ContourTexture> {
        if (!src.signedDistanceField) {
            throw new Error('Cannot apply margin: source has no SDF data');
        }

        if (src.signedDistanceField.toArray().length === 0) {
            throw new Error('Cannot apply margin: source SDF is empty');
        }

        const falloffMm = ContourTexture.DEFAULT_MARGIN;
        const renderFalloffMm = widenFalloff
            ? maxAbsMarginMm + falloffMm
            : falloffMm;

        const expanded = ContourTexture.createNewWithMargin(
            maxExpansionMm,
            src
        );

        const renderer = RenderStorage.getRenderer(SDFOperationRenderer);
        renderer.renderOnDestination(expanded, BooleanOperation.OR, src);

        const sdfGenerator = RenderStorage.getRenderer(SdfGenerator);
        const pixels = await sdfGenerator.renderSDFFromTexture(
            expanded,
            renderFalloffMm,
            renderFalloffMm,
            true
        );

        expanded.resetSdfAndGrid(pixels, expanded.textureGrid);
        const shifted = await applyShift(expanded, renderFalloffMm);

        const outArrays = widenFalloff
            ? SDFOperations.decreaseFalloff(shifted, renderFalloffMm, falloffMm)
            : shifted;

        expanded.resetSdfAndGrid(outArrays, expanded.textureGrid);
        // Warm the pixel cache for the branch where cropToImageBounds
        // early-returns (texture already within image bounds), so a
        // downstream CPU consumer does not pay a full readback.
        // `outArrays` is freshly built above and byte-identical to the
        // upload, so holding the reference is aliasing-safe.
        expanded.signedDistanceField?.setCachedPixels(outArrays);
        expanded.cropToImageBounds();
        return expanded;
    }

    /**
     * Remove small disconnected parts from an SDF texture.
     * Parts below the mode-specific size threshold are removed (component
     * volume in 3D, in-plane component area in 2D).
     *
     * Rewrites only the removed components' voxels; every other voxel is
     * bit-identical to the source. The whole volume must not be
     * reinitialized afterwards: that round trip is contractive, and shrinks
     * structures only a few voxels across.
     *
     * An 'unchanged' outcome carries no texture; commit nothing then.
     *
     * @param src Source ContourTexture
     * @param minSize Minimum component size to keep — cm³ (volume) in 3D mode, cm² (in-plane area) in 2D mode (default: 0.5)
     * @param mode '3D' evaluates components by volume over the whole volume; '2D' by in-plane area per native slice (default: '3D')
     * @param signal Optional AbortSignal for cancellation
     * @param options Forwarded to `removeSmallParts`, except that removed parts always go to the encoding floor here
     * @returns Stats plus, when something was removed, a new ContourTexture with the parts removed; undefined if source has no SDF
     */
    public static async applyRemoveSmallParts(
        src: ContourTexture,
        minSize: number = 0.5,
        mode: PostProcessingMode = '3D',
        signal?: AbortSignal,
        options?: RemoveSmallPartsOptions
    ): Promise<ComponentOperationOutcome | undefined> {
        if (!GlOperationGuard.acquire('Remove small parts')) {
            return undefined;
        }
        try {
            const extracted = SDFOperations.extractSdfPixelData(src);
            if (!extracted) {
                return undefined;
            }

            const { srcPixels, nx, ny, nz, threshold } = extracted;

            const { resX, resY, resZ } = src.textureGrid;
            // Convert the UI threshold to domain (mm) units for the mode: 2D
            // thresholds by in-plane area (cm² → mm²), 3D by volume (cm³ → mm³).
            const minComponentSizeMm =
                mode === '2D' ? minSize * 100 : minSize * 1000;

            const {
                sdfU8,
                numComponentsFound,
                numComponentsRemoved,
                sizeFloorOverridden,
            } = await removeSmallParts(
                srcPixels,
                nx,
                ny,
                nz,
                threshold,
                resX,
                resY,
                resZ,
                minComponentSizeMm,
                mode,
                signal,
                options
            );

            if (numComponentsRemoved === 0) {
                return {
                    status: 'unchanged',
                    numComponentsFound,
                    numComponentsRemoved: 0,
                    sizeFloorOverridden,
                };
            }

            const texture = src.cloneWithNewSdf(sdfU8, { adoptPixels: true });

            return {
                status: 'changed',
                texture,
                numComponentsFound,
                numComponentsRemoved,
                sizeFloorOverridden,
            };
        } finally {
            GlOperationGuard.release();
        }
    }

    /**
     * Fill cavities (holes) in an SDF texture.
     * Any enclosed cavity inside the structure will be filled.
     *
     * Commits {@link fillCavities}' output as it stands: it patches only
     * cavity voxels (to 255), so it is already the union with the source.
     * The whole volume must not be reinitialized afterwards, for the reason
     * given on {@link applyRemoveSmallParts}. Filled voxels therefore read
     * as saturated interior rather than metric distance, which leaves the
     * iso surface unmoved: a cavity is enclosed, so its neighbours are
     * already inside, and the margin operations rebuild the field from the
     * iso before using it.
     *
     * An 'unchanged' outcome carries no texture; commit nothing then.
     *
     * @param src Source ContourTexture
     * @param mode '3D' fills cavities enclosed by the whole volume; '2D' per native slice (default: '3D')
     * @param signal Optional AbortSignal for cancellation
     * @returns Number of voxels filled plus, when it is > 0, a new ContourTexture with the cavities filled; undefined if source has no SDF
     */
    public static async applyFillCavities(
        src: ContourTexture,
        mode: PostProcessingMode = '3D',
        signal?: AbortSignal
    ): Promise<FillCavitiesOutcome | undefined> {
        if (!GlOperationGuard.acquire('Fill cavities')) {
            return undefined;
        }
        try {
            const extracted = SDFOperations.extractSdfPixelData(src);
            if (!extracted) {
                return undefined;
            }

            const { srcPixels, nx, ny, nz, threshold } = extracted;

            const { sdfU8, filledVoxelCount } = await fillCavities(
                srcPixels,
                nx,
                ny,
                nz,
                threshold,
                mode,
                signal
            );

            if (filledVoxelCount === 0) {
                return { status: 'unchanged', filledVoxelCount: 0 };
            }

            const texture = src.cloneWithNewSdf(sdfU8, { adoptPixels: true });
            return { status: 'changed', texture, filledVoxelCount };
        } finally {
            GlOperationGuard.release();
        }
    }

    /**
     * Fill enclosed holes per slice (2D) in an SDF texture — the same
     * {@link fillCavities2D} primitive the threshold segmentation's
     * mask-phase and final fills run, lifted to the texture level. Unlike
     * {@link applyFillCavities} (3D), a region is a hole whenever the
     * slice encloses it in-plane, even when it connects to outside air
     * through another slice — see the primitive's contract for why the
     * two disagree on lung-like anatomy.
     *
     * @param src Source ContourTexture. Read-only; the CALLER owns it.
     * @param signal Optional AbortSignal for cancellation
     * @returns New ContourTexture the caller owns, or undefined if the GL
     *          guard is held by another operation or the source has no SDF
     */
    public static async applyFillCavities2D(
        src: ContourTexture,
        signal?: AbortSignal
    ): Promise<ContourTexture | undefined> {
        if (!GlOperationGuard.acquire('Fill holes (2D)')) {
            return undefined;
        }
        try {
            const extracted = SDFOperations.extractSdfPixelData(src);
            if (!extracted) {
                return undefined;
            }

            const { srcPixels, nx, ny, nz, threshold } = extracted;

            const { sdfU8: processedU8 } = await fillCavities2D(
                srcPixels,
                nx,
                ny,
                nz,
                threshold,
                signal
            );

            return SDFOperations.gpuReinitAndClone(processedU8, src);
        } finally {
            GlOperationGuard.release();
        }
    }

    /**
     * Reinitialize an existing Uint8 SDF using the WebGL-based SDFMesh pipeline.
     * Extracts iso-contours via marching squares and renders them through the GPU
     * SDF pipeline, producing smoother distance gradients than CPU-based FMM.
     *
     * @param src Source ContourTexture containing the SDF to reinitialize
     * @param fullDistanceField When true, distances also run through the
     *        slices; when false, each slice holds the distance to its own
     *        contour.
     * @returns New ContourTexture with reinitialized SDF, or undefined if source has no SDF
     */
    public static async reinitializeSDFWebGL(
        src: ContourTexture,
        fullDistanceField: boolean = true
    ): Promise<ContourTexture | undefined> {
        if (!src.signedDistanceField) {
            return undefined;
        }

        // Acquire the GL guard if not already held by the caller
        // (e.g. applyGaussianSmoothingSigmaGL already holds it).
        const needsGuard = !GlOperationGuard.isActive;
        if (needsGuard && !GlOperationGuard.acquire('SDF reinitialization')) {
            return undefined;
        }
        try {
            // Read src pixels via the caching accessor (free on
            // subsequent calls).
            return await SDFOperations.gpuReinitAndClone(
                src.pixels,
                src,
                fullDistanceField
            );
        } finally {
            if (needsGuard) {
                GlOperationGuard.release();
            }
        }
    }

    /**
     * Reinitialize like {@link reinitializeSDFWebGL}, but return the LIVE
     * GPU write target instead of reading the volume back to the CPU and
     * cloning. Use this when the next consumer is another GPU pass (the
     * Gaussian smoothing blur): the readback + re-upload of the CPU
     * variant existed only to build a ContourTexture wrapper, costing two
     * full-volume synchronous transfers per preview.
     *
     * The returned texture holds only the reinitialized field — no CPU
     * pixel cache, and none of `src`'s metadata beyond the grid. The
     * CALLER OWNS it and must `delete()` it (release with acquisition);
     * on any throw after the target is created, it is released here so
     * nothing leaks on the exceptional path.
     */
    public static async reinitializeSDFWebGLToTexture(
        src: ContourTexture
    ): Promise<ContourTexture | undefined> {
        if (!src.signedDistanceField) {
            return undefined;
        }

        // Acquire the GL guard if not already held by the caller
        // (e.g. applyGaussianSmoothingSigmaGL already holds it).
        const needsGuard = !GlOperationGuard.isActive;
        if (needsGuard && !GlOperationGuard.acquire('SDF reinitialization')) {
            return undefined;
        }
        const falloffMm = ContourTexture.DEFAULT_MARGIN;
        const writeTarget = new ContourTexture(
            src.grid,
            src.sizePx,
            src.textureGrid.getPosition(),
            src.margin
        );
        const sdfGenerator = new SdfGenerator();
        try {
            await sdfGenerator.renderSDFFromPixelDataToTexture(
                writeTarget,
                falloffMm,
                falloffMm,
                src.pixels,
                true // fullDistanceField: geometry ops need true 3D distance
            );
            return writeTarget;
        } catch (e) {
            writeTarget.delete();
            throw e;
        } finally {
            sdfGenerator.delete();
            if (needsGuard) {
                GlOperationGuard.release();
            }
        }
    }

    /**
     * Guard + extract SDF pixel data and grid dimensions from a ContourTexture.
     * Returns undefined if the source has no valid SDF data.
     */
    private static extractSdfPixelData(src: ContourTexture):
        | {
              srcPixels: Uint8Array[];
              nx: number;
              ny: number;
              nz: number;
              threshold: number;
              falloffMm: number;
          }
        | undefined {
        if (!src.signedDistanceField) {
            return undefined;
        }

        const srcPixels = src.signedDistanceField.toArray();
        if (!srcPixels) {
            return undefined;
        }

        return {
            srcPixels,
            nx: src.textureGrid.sizeX,
            ny: src.textureGrid.sizeY,
            nz: src.textureGrid.sizeZ,
            threshold: ContourTexture.SDF_THRESHOLD,
            falloffMm: ContourTexture.DEFAULT_MARGIN,
        };
    }

    /**
     * Reinitialize a modified SDF via the GPU pipeline and return a new
     * ContourTexture cloned from the source with the reinitialized SDF.
     */
    private static async gpuReinitAndClone(
        processedU8: Uint8Array[],
        src: ContourTexture,
        fullDistanceField: boolean = true
    ): Promise<ContourTexture> {
        const falloffMm = ContourTexture.DEFAULT_MARGIN;
        const writeTarget = new ContourTexture(
            src.grid,
            src.sizePx,
            src.textureGrid.getPosition(),
            src.margin
        );
        const sdfGenerator = new SdfGenerator();
        // Both are released on the throwing readback too, the shape
        // reinitializeSDFWebGLToTexture already uses. Without it a rejected
        // readback leaks the generator and the write target.
        let reinitializedU8: Uint8Array[];
        try {
            reinitializedU8 = await sdfGenerator.renderSDFFromPixelData(
                writeTarget,
                falloffMm,
                falloffMm,
                processedU8,
                fullDistanceField
            );
        } finally {
            sdfGenerator.delete();
            writeTarget.delete();
        }

        return src.cloneWithNewSdf(reinitializedU8, { adoptPixels: true });
    }

    /**
     * Shift an SDF by a constant margin, preserving the falloff encoding.
     * Note that this will also shift voxels far away from the surface so applying
     * multiple margins sequentially will eventually invalidate the sdf boundary.
     *
     * @param pixels SDF slices encoded with {@link falloffMm}
     * @param marginMm Expansion (positive) or erosion (negative) in mm
     * @param falloffMm Falloff distance of the encoding
     * @returns Shifted SDF slices in the same encoding
     */
    private static applySymmetricShift(
        pixels: Uint8Array[],
        marginMm: number,
        falloffMm: number = ContourTexture.DEFAULT_MARGIN
    ): Uint8Array[] {
        const delta = marginMm * (ContourTexture.SDF_THRESHOLD / falloffMm);
        const outArrays = new Array<Uint8Array>(pixels.length);

        for (let z = 0; z < pixels.length; z++) {
            const slice = pixels[z];
            const outSlice = new Uint8Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                const val = slice[i] + delta;
                outSlice[i] = val < 0 ? 0 : val > 255 ? 255 : Math.round(val);
            }
            outArrays[z] = outSlice;
        }

        return outArrays;
    }

    /**
     * Re-encode an SDF from a larger falloff to a smaller falloff.
     *
     * @param pixels SDF slices encoded with {@link inputFalloffMm}
     * @param inputFalloffMm Falloff distance of the input encoding
     * @param outputFalloffMm Falloff distance for the output encoding. Must be smaller than {@link inputFalloffMm}
     * @returns SDF slices re-encoded with {@link outputFalloffMm}
     */
    private static decreaseFalloff(
        pixels: Uint8Array[],
        inputFalloffMm: number,
        outputFalloffMm: number
    ): Uint8Array[] {
        if (inputFalloffMm < outputFalloffMm) {
            throw new Error(
                `Input falloff (${inputFalloffMm}mm) must be greater than output falloff (${outputFalloffMm}mm)`
            );
        }
        const threshold = ContourTexture.SDF_THRESHOLD;
        const scale = inputFalloffMm / outputFalloffMm;
        const outArrays = new Array<Uint8Array>(pixels.length);

        for (let z = 0; z < pixels.length; z++) {
            const slice = pixels[z];
            const outSlice = new Uint8Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                const val = threshold + (slice[i] - threshold) * scale;
                outSlice[i] = val < 0 ? 0 : val > 255 ? 255 : Math.round(val);
            }
            outArrays[z] = outSlice;
        }

        return outArrays;
    }

    /**
     * Restrict an ROI to a world-space box, or clear what the box contains.
     *
     * The box is resolved against the ROI's OWN texture grid, not the image
     * grid: the two need not share a sampling, and the SDF is what is being
     * cropped.
     *
     * The box must hold some of the structure. A box clear of the contours
     * but still inside the texture's margin band resolves to a NON-empty
     * voxel window, so the window test alone would let 'keep-inside' commit
     * an emptied structure under a success message; the foreground the run
     * retains is what decides.
     *
     * @param src Source ContourTexture
     * @param boundsMm Region in world mm, as the shared VOI overlay edits it
     * @param direction Keep what the box contains, or clear it
     * @param signal Optional AbortSignal for cancellation
     * @throws EmptyCropRegionError when the box holds no part of the
     * structure
     * @throws GlOperationBusyError when another GL operation is running
     */
    public static async applyCropToBounds(
        src: ContourTexture,
        boundsMm: WorldBoundsMm,
        direction: CropDirection,
        signal?: AbortSignal
    ): Promise<
        { texture: ContourTexture; clearedVoxelCount: number } | undefined
    > {
        if (GlOperationGuard.isActive) {
            // Thrown rather than returned as undefined: the run did not
            // happen, which a caller answers by re-offering it, where
            // undefined ("ran, produced nothing") is an outcome to report.
            //
            // Pre-checked the way useBulkUnionPreview checks, rather than
            // read off a refused `acquire`: this is the one wrapper whose
            // caller RETRIES on contention, and acquire logs every refusal
            // as the defect it is for everyone else — a caller waiting the
            // guard out would post one "should never happen" error per
            // macrotask for the whole wait.
            throw new GlOperationBusyError('Crop to region');
        }
        // Cannot fail: the check above and this acquisition are one
        // synchronous step, and nothing else runs between them.
        GlOperationGuard.acquire('Crop to region');
        try {
            const extracted = SDFOperations.extractSdfPixelData(src);
            if (!extracted) {
                return undefined;
            }
            const { srcPixels, nx, ny, nz, threshold } = extracted;

            // gridSubRange is used ONLY to refuse: it clamps to the grid,
            // so an off-anatomy box arrives as an empty window rather than
            // an exception, and the refusal stays legible as "move the box".
            // It is not what the cut runs on — snapped outward, it would
            // place the surface up to a voxel per face beyond what the user
            // drew.
            if (subRangeIsEmpty(gridSubRange(src.textureGrid, boundsMm))) {
                throw new EmptyCropRegionError(
                    'Crop region does not intersect the structure grid'
                );
            }

            const grid = src.textureGrid;
            const { sdfU8, clearedVoxelCount, retainedVoxelCount } =
                await cropToBounds(
                    srcPixels,
                    nx,
                    ny,
                    nz,
                    threshold,
                    gridVoxelBounds(grid, boundsMm),
                    [grid.resX, grid.resY, grid.resZ],
                    src.sdfBytesPerMm(),
                    direction,
                    signal
                );

            if (direction === 'keep-inside' && retainedVoxelCount === 0) {
                // Keeping only what a box holds, when it holds none of the
                // structure, empties it. Refused before anything is
                // allocated, so the answer stays "reposition the box"
                // rather than a generic refusal of an empty result.
                throw new EmptyCropRegionError(
                    'Crop region contains no part of the structure'
                );
            }

            const texture = await SDFOperations.gpuReinitAndClone(sdfU8, src);
            return { texture, clearedVoxelCount };
        } finally {
            GlOperationGuard.release();
        }
    }

    /**
     * Clear whole parts touching a voxel window given DIRECTLY, without a
     * world box in between.
     *
     * A window that is already known in voxels — the single voxel a pick
     * resolved to — must not be expressed as a world box and converted
     * back. Its faces then lie exactly on voxel boundaries, the transforms
     * are float32, and {@link gridSubRange} rounds outward: a rounding
     * error of one ulp on either face widens the window by a whole voxel,
     * and {@link clearPartsTouchingWindow} takes every part that window
     * touches, on every slice in it. A pick at one curve then deletes the
     * curve running alongside it.
     */
    public static async applyClearPartsInWindow(
        src: ContourTexture,
        range: VoxelSubRange
    ): Promise<
        | {
              texture: ContourTexture;
              clearedVoxelCount: number;
              clearedPartCount: number;
          }
        | undefined
    > {
        if (!GlOperationGuard.acquire('Delete curves in window')) {
            return undefined;
        }
        try {
            return await clearPartsInWindow(src, range);
        } finally {
            GlOperationGuard.release();
        }
    }

    /**
     * Keep the N largest connected parts, optionally with a size floor.
     *
     * The floor is FUSED into the one labeling pass rather than run as a
     * second {@link applyRemoveSmallParts} call — see {@link ComponentSizeFloor}
     * for why one labeling is sufficient.
     *
     * @param minSize Optional floor — cm3 (volume) in 3D, cm2 (in-plane area)
     *   in 2D, matching {@link applyRemoveSmallParts}. Undefined means no floor.
     */
    public static async applyKeepLargestComponents(
        src: ContourTexture,
        keepCount: number,
        mode: PostProcessingMode = '3D',
        minSize?: number,
        signal?: AbortSignal
    ): Promise<ComponentOperationOutcome | undefined> {
        if (!GlOperationGuard.acquire('Keep largest parts')) {
            return undefined;
        }
        try {
            const extracted = SDFOperations.extractSdfPixelData(src);
            if (!extracted) {
                return undefined;
            }
            const { srcPixels, nx, ny, nz, threshold } = extracted;
            const { resX, resY, resZ } = src.textureGrid;

            const sizeFloor: ComponentSizeFloor | undefined =
                minSize === undefined
                    ? undefined
                    : {
                          // Same conversion as applyRemoveSmallParts: 2D
                          // thresholds by in-plane area (cm2 -> mm2), 3D by
                          // volume (cm3 -> mm3).
                          minComponentSizeMm:
                              mode === '2D' ? minSize * 100 : minSize * 1000,
                          resXMm: resX,
                          resYMm: resY,
                          resZMm: resZ,
                      };

            const {
                sdfU8,
                numComponentsFound,
                numComponentsRemoved,
                sizeFloorOverridden,
            } = await keepLargestComponents(
                srcPixels,
                nx,
                ny,
                nz,
                threshold,
                keepCount,
                signal,
                { connectivity: mode === '2D' ? '2d' : '3d' },
                sizeFloor
            );

            // Nothing removed is a SUCCESS that writes no texture: the
            // structure already held only the parts the user asked to
            // keep, so a tracked write would cost them an undo press to
            // get back to what they already had. Reported either way.
            // The GPU reinit is skipped with it — there is nothing to
            // reinitialise.
            if (numComponentsRemoved === 0) {
                return {
                    status: 'unchanged',
                    numComponentsFound,
                    numComponentsRemoved: 0,
                    sizeFloorOverridden,
                };
            }

            const texture = await SDFOperations.gpuReinitAndClone(sdfU8, src);
            return {
                status: 'changed',
                texture,
                numComponentsFound,
                numComponentsRemoved,
                sizeFloorOverridden,
            };
        } finally {
            GlOperationGuard.release();
        }
    }
}
